import { CountryGroup } from '../internationalisation/classes/countryGroup';
import { countryGroupMatches, getParticipationFromQueryString, randomNumber, } from './helpers';
import { getMvtId } from './mvt';
import { getSessionParticipations, setSessionParticipations, } from './sessionStorage';
/**
 * Generic function to get A/B test participations for any page type.
 *
 * This function will always return a variant, regardless of which page the user is on.
 * We sometimes need these settings on other pages as well.
 *
 * If the user is on the target page, or session storage contains a participation,
 * then it will also return the participations data for tracking.
 * Otherwise we assume the user has not arrived via the target page, and the participations
 * object will be empty because we do not need to track it.
 */
export function getPageParticipations(config) {
    const countryGroupId = CountryGroup.detect();
    const path = window.location.pathname;
    const mvtId = getMvtId();
    const queryString = window.location.search;
    const { tests, pageRegex, forceParamName, sessionStorageKey, fallbackVariant, fallbackParticipationKey, getVariantName, } = config;
    const isTargetPage = (path) => !!path && !!path.match(pageRegex);
    const getVariant = (participations, testList) => {
        for (const test of testList) {
            const variantName = participations[test.name];
            if (variantName) {
                const variant = test.variants.find((v) => getVariantName(v) === variantName);
                if (variant) {
                    return variant;
                }
            }
        }
        return fallbackVariant(countryGroupId);
    };
    // Is the participation forced in the url querystring?
    const urlParticipations = getParticipationFromQueryString(queryString, forceParamName);
    if (urlParticipations) {
        const variant = getVariant(urlParticipations, tests);
        return {
            participations: urlParticipations,
            variant,
        };
    }
    // Is there already a participation in session storage?
    const sessionParticipations = getSessionParticipations(sessionStorageKey);
    if (sessionParticipations &&
        Object.entries(sessionParticipations).length > 0) {
        const variant = getVariant(sessionParticipations, tests);
        return {
            participations: sessionParticipations,
            variant,
        };
    }
    // No participation in session storage, assign user to a test + variant
    const test = tests
        .filter((test) => test.status === 'Live')
        .find((test) => {
        return countryGroupMatches(test.regionTargeting?.targetedCountryGroups, countryGroupId);
    });
    // Only track participation if user is on the target page
    const trackParticipation = isTargetPage(path);
    if (test) {
        const idx = randomNumber(mvtId, test.name) % test.variants.length;
        const variant = test.variants[idx];
        if (variant) {
            const participations = {
                [test.name]: getVariantName(variant),
            };
            // Record the participation in session storage so that we can track it from other pages
            setSessionParticipations(participations, sessionStorageKey);
            return {
                participations: trackParticipation
                    ? participations
                    : {},
                variant,
            };
        }
    }
    // No test found, use the fallback
    const fallback = fallbackVariant(countryGroupId);
    return {
        participations: trackParticipation
            ? { [fallbackParticipationKey]: getVariantName(fallback) }
            : {},
        variant: fallback,
    };
}
